import { Colors, Icon, Text } from '@amelio/ui-library';

import {
    TILE_ACTIVE,
    TILE_BASE,
    TILE_ICON_WRAPPER,
    TILE_INACTIVE,
    VIEW_TILES_GRID,
} from '../calibrationStyles';
import { DEFAULT_MANAGER_PIVOT, isManagerView } from '../utils/calibrationViews';

import type { CalibrationView } from '@/types/calibration';
import type { IconProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

/** The two entry points the report opens on. "Managers" covers both manager pivots, which the
 * card below switches between with its own tab strip. */
type ViewTileKey = 'managers' | 'employees';

interface ViewTileDefinition {
    tile: ViewTileKey;
    iconName: IconProps['name'];
}

const VIEW_TILE_DEFINITIONS: ViewTileDefinition[] = [
    { tile: 'managers', iconName: 'users-people' },
    { tile: 'employees', iconName: 'essential-judge' },
];

export interface ViewTilesProps {
    activeView: CalibrationView;
    onViewChange: (view: CalibrationView) => void;
    /** The "Employee calibration" tile only exists for a caller who can manage adjustments, so
     * there is no dead entry point: a team manager who cannot edit adjustments never sees a tile
     * for a view they cannot act on. */
    canViewEmployees: boolean;
}

/**
 * The two view tiles at the top of the report. Each tile is a full `button` — a tile is
 * entirely clickable, never just an icon or a label inside it — toggled by click and by
 * keyboard, and announces its selection with `aria-pressed`.
 */
export const ViewTiles = ({ activeView, onViewChange, canViewEmployees }: ViewTilesProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    const visibleTiles = VIEW_TILE_DEFINITIONS.filter(
        (definition) => definition.tile !== 'employees' || canViewEmployees,
    );

    // Coming back from the employee view lands on the default pivot; clicking the tile that is
    // already active keeps whichever pivot the tab strip is showing.
    const viewForTile = (tile: ViewTileKey): CalibrationView => {
        if (tile === 'employees') {
            return 'employees';
        }

        return isManagerView(activeView) ? activeView : DEFAULT_MANAGER_PIVOT;
    };

    return (
        <div
            className={VIEW_TILES_GRID}
            role={'group'}
            aria-label={t('calibration.views.groupLabel')}
        >
            {visibleTiles.map(({ tile, iconName }) => {
                const isActive =
                    tile === 'employees' ? activeView === 'employees' : isManagerView(activeView);

                return (
                    <button
                        key={tile}
                        type={'button'}
                        aria-pressed={isActive}
                        onClick={() => onViewChange(viewForTile(tile))}
                        className={`${TILE_BASE} ${isActive ? TILE_ACTIVE : TILE_INACTIVE}`}
                        data-testid={`calibration-view-tile-${tile}`}
                    >
                        <div className={TILE_ICON_WRAPPER}>
                            <Icon
                                name={iconName}
                                color={Colors.PRIMARY}
                                size={'lg'}
                                ariaHidden
                            />
                        </div>

                        <div className={'flex min-w-0 flex-col gap-0.5'}>
                            <Text
                                weight={'bold'}
                                color={Colors.NAVY}
                            >
                                {t(`calibration.views.${tile}.title`, {
                                    managerLabel: getManagerLabel(),
                                })}
                            </Text>

                            <Text color={Colors.BASE_500}>
                                {t(`calibration.views.${tile}.description`, {
                                    managerLabel: getManagerLabel(),
                                })}
                            </Text>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};
